// ── Viewport culling ──
// Extracted verbatim from MapCanvas.tsx. Hides map objects outside the visible
// viewport (point test for markers, local-bounds test for the large island
// sprites) and re-runs throttled via rAF on viewport `moved` / `zoomed`.
//
// The effect body is byte-identical to the original; every closure dependency
// (scene refs + the two gating values) is a parameter under its ORIGINAL name.
import { useEffect, type MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import { getWrapPeriod } from './worldWrap';
import { rectContains, rectsOverlap, visibleWorldRects } from './worldWrapMath';

export interface UseMapCullingParams {
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
  /** Effect deps — by value, re-read every render. */
  isPixiAppReady: boolean;
  mapObjectsRenderKey: string;
}

export function useMapCulling({
  sceneRef,
  isPixiAppReady,
  mapObjectsRenderKey,
}: UseMapCullingParams): void {
  // Culling: приховуємо об'єкти поза viewport для оптимізації рендерингу
  useEffect(() => {
    if (!sceneRef.current.pixi.app || !sceneRef.current.pixi.viewport || !sceneRef.current.layers.mapObjects || !isPixiAppReady) {
      return;
    }

    // Margin in world coords. Map markers use cheap point tests; islands use bounds (see below).
    const CULL_MARGIN = 300;

    const cullOffscreenObjects = () => {
      if (!sceneRef.current.pixi.viewport || !sceneRef.current.layers.mapObjects) return;

      const viewport = sceneRef.current.pixi.viewport;
      const topLeft = viewport.toWorld(0, 0);
      const bottomRight = viewport.toWorld(viewport.screenWidth, viewport.screenHeight);

      const left = topLeft.x - CULL_MARGIN;
      const right = bottomRight.x + CULL_MARGIN;
      const top = topLeft.y - CULL_MARGIN;
      const bottom = bottomRight.y + CULL_MARGIN;
      // The map wraps around (worldWrap.ts): the far side of the world shows up
      // in copies near the edges, so it must stay drawn too.
      const rects = visibleWorldRects({ minX: left, minY: top, maxX: right, maxY: bottom }, getWrapPeriod());

      // Houses / plots / markers: anchor near object center — point + margin is cheap and accurate enough.
      const cullLayerByPosition = (layer: PIXI.Container) => {
        for (const child of layer.children) {
          if (!child.cullable) continue;
          child.renderable = rects.some((r) => rectContains(r, child.x, child.y));
        }
      };

      // Islands are large sprites; (x,y) is often not where most pixels are → point culling hides them early.
      const cullLayerByLocalBounds = (layer: PIXI.Container) => {
        for (const child of layer.children) {
          if (!child.cullable) continue;
          try {
            const localBounds = child.getLocalBounds();
            const bounds = {
              minX: child.x + localBounds.x,
              maxX: child.x + localBounds.x + localBounds.width,
              minY: child.y + localBounds.y,
              maxY: child.y + localBounds.y + localBounds.height,
            };
            child.renderable = rects.some((r) => rectsOverlap(r, bounds));
          } catch {
            child.renderable = rects.some((r) => rectContains(r, child.x, child.y));
          }
        }
      };

      cullLayerByPosition(sceneRef.current.layers.mapObjects);
      if (sceneRef.current.layers.island) cullLayerByLocalBounds(sceneRef.current.layers.island);
    };

    // Throttle via rAF — at most once per frame
    let cullScheduled = false;
    const scheduleCull = () => {
      if (cullScheduled) return;
      cullScheduled = true;
      requestAnimationFrame(() => {
        cullScheduled = false;
        cullOffscreenObjects();
      });
    };

    cullOffscreenObjects();
    const viewport = sceneRef.current.pixi.viewport;
    viewport.on('moved', scheduleCull);
    viewport.on('zoomed', scheduleCull);

    return () => {
      viewport.off('moved', scheduleCull);
      viewport.off('zoomed', scheduleCull);
    };
  }, [isPixiAppReady, mapObjectsRenderKey, sceneRef]);
}
